/* eslint-disable indent */
const STICKY_MIN_SPACE = 500;
const MOBILE_MAX = 799;

class SideTabs {
	constructor(el) {
		this.el = el;
		this.isHorizontal = !!this.el.querySelector('.block-side-tabs--horizontal');
		this.isVerticalTwoTones = !!this.el.querySelector('.block-side-tabs--vertical-two-tones');
		this.tablist = this.el.querySelector('[role="tablist"]');
		this.tablist?.setAttribute('aria-orientation', this.isHorizontal ? 'horizontal' : 'vertical');
		this.tabs = Array.from(this.el.querySelectorAll('[role="tab"]'));
		this.tabs.forEach((tab) => {
			tab.tabIndex = tab.classList.contains('active') ? 0 : -1;
		});
		this.tabContentContainer = Array.from(
			this.el.querySelectorAll('.block-side-tabs__tab-content-container')
		);
		this.tabPanels = Array.from(this.el.querySelectorAll('[role="tabpanel"]'));
		this.tabsRow = this.el.querySelector('.block-side-tabs__tabs-row');
		this.tabsEl = this.el.querySelector('.block-side-tabs__tabs');

		this.handleTabClick();
		this.handleTabFocus();
		this.handleTabKeydown();
		this.handleAccordionClick();
		this.syncResponsiveState();
		this.updateStickyState();

		this.el.querySelectorAll('img').forEach((img) => {
			if (!img.complete) {
				img.addEventListener('load', () => this.updateStickyState(), { once: true });
			}
		});

		window.addEventListener('resize', () => {
			this.syncResponsiveState();
			this.updateStickyState();
		});
	}

	isMobile() {
		return window.innerWidth <= MOBILE_MAX;
	}

	getRemainingStickySpace() {
		if (!this.tabsRow || !this.tabsEl) {
			return 0;
		}

		const tabsStyle = window.getComputedStyle(this.tabsEl);
		const marginTop = parseFloat(tabsStyle.marginTop) || 0;
		const marginBottom = parseFloat(tabsStyle.marginBottom) || 0;
		const tabsHeight = this.tabsEl.offsetHeight + marginTop + marginBottom;

		return this.tabsRow.offsetHeight - tabsHeight;
	}

	updateStickyState() {
		if (!this.tabsEl || this.isHorizontal || this.isVerticalTwoTones || this.isMobile()) {
			this.tabsEl?.classList.remove('block-side-tabs__tabs--sticky');
			return;
		}

		const remainingSpace = this.getRemainingStickySpace();

		if (remainingSpace >= STICKY_MIN_SPACE) {
			this.tabsEl.classList.add('block-side-tabs__tabs--sticky');
		} else {
			this.tabsEl.classList.remove('block-side-tabs__tabs--sticky');
		}
	}

	/**
	 * Mobile accordion needs all panels in the DOM (headers visible).
	 * Desktop tabs should hide inactive panels from AT via the hidden attribute.
	 */
	syncResponsiveState() {
		const mobile = this.isMobile();

		// On desktop, always make sure one tab is active.
		// If mobile left all accordions closed, activate the first tab.
		if (!mobile) {
			const activeContainer = this.tabContentContainer.find((container) =>
				container.classList.contains('active')
			);

			if (!activeContainer && this.tabs.length) {
				this.activateTab(this.tabs[0].dataset.index);
				return;
			}
		}

		this.tabContentContainer.forEach((container) => {
			const panel = container.querySelector('[role="tabpanel"]');
			const isActive = container.classList.contains('active');

			if (!panel) {
				return;
			}

			if (mobile) {
				panel.hidden = false;
				panel.inert = !isActive;
			} else {
				panel.hidden = !isActive;
				panel.inert = false;
			}
		});

		if (this.tablist) {
			this.tablist.setAttribute('aria-hidden', mobile ? 'true' : 'false');
		}
	}

	activateTab(index) {
		const indexStr = String(index);

		this.tabs.forEach((tab) => {
			const isActive = tab.dataset.index === indexStr;

			tab.classList.toggle('active', isActive);
			tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
			tab.tabIndex = isActive ? 0 : -1;
		});

		this.tabContentContainer.forEach((container) => {
			const isActive = container.dataset.index === indexStr;
			const titleButton = container.querySelector('.block-side-tabs__tab-content-title-button');
			const panel = container.querySelector('[role="tabpanel"]');

			container.classList.toggle('active', isActive);

			if (titleButton) {
				titleButton.setAttribute('aria-expanded', isActive ? 'true' : 'false');
			}

			if (panel) {
				if (this.isMobile()) {
					panel.hidden = false;
					panel.inert = !isActive;
				} else {
					panel.hidden = !isActive;
					panel.inert = false;
				}
			}
		});

		requestAnimationFrame(() => {
			this.updateStickyState();
		});
	}

	handleTabClick = () => {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				if (this.isMobile()) {
					return;
				}

				this.activateTab(tab.dataset.index);
			});
		});
	};

	/**
	 * All tabs stay in the natural tab order so Tab / Shift+Tab moves
	 * through the side-tab list. Focusing a tab also activates its panel.
	 */
	handleTabFocus = () => {
		this.tabs.forEach((tab) => {
			tab.addEventListener('focus', () => {
				if (this.isMobile()) {
					return;
				}

				this.activateTab(tab.dataset.index);
			});
		});
	};

	// add option to move on tabs with arrows
	handleTabKeydown = () => {
		this.tabs.forEach((tab, index) => {
			tab.addEventListener('keydown', (event) => {
				if (this.isMobile()) {
					return;
				}
				const nextKey = this.isHorizontal ? 'ArrowRight' : 'ArrowDown';
				const prevKey = this.isHorizontal ? 'ArrowLeft' : 'ArrowUp';
				let nextIndex = null;
				if (event.key === nextKey) {
					nextIndex = (index + 1) % this.tabs.length;
				} else if (event.key === prevKey) {
					nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
				} else if (event.key === 'Home') {
					nextIndex = 0;
				} else if (event.key === 'End') {
					nextIndex = this.tabs.length - 1;
				}
				if (nextIndex === null) {
					return;
				}
				event.preventDefault();
				const nextTab = this.tabs[nextIndex];
				this.activateTab(nextTab.dataset.index);
				nextTab.focus();
			});
		});
	};

	handleAccordionClick = () => {
		this.tabContentContainer.forEach((tabContent) => {
			const titleButton = tabContent.querySelector('.block-side-tabs__tab-content-title-button');

			if (!titleButton) {
				return;
			}

			titleButton.addEventListener('click', () => {
				if (!this.isMobile()) {
					return;
				}

				const isOpen = tabContent.classList.contains('active');

				this.tabContentContainer.forEach((container) => {
					const button = container.querySelector('.block-side-tabs__tab-content-title-button');
					const shouldOpen =
						!isOpen && container.dataset.index === titleButton.dataset.index;

					container.classList.toggle('active', shouldOpen);

					if (button) {
						button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
					}
				});

				// Keep desktop tab selection in sync for when viewport grows.
				if (!isOpen) {
					this.tabs.forEach((tab) => {
						const isActive = tab.dataset.index === titleButton.dataset.index;

						tab.classList.toggle('active', isActive);
						tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
						tab.tabIndex = isActive ? 0 : -1;
					});
				}
				this.syncResponsiveState();
			});
		});
	};
}

export default SideTabs;
